import { Button } from '@repo/ui/components/button';
import { Table, TableBody, TableCell, TableRow } from '@repo/ui/components/table';
import { IconAlertTriangle, IconWifiOff } from '@tabler/icons-react';
import type { ReactNode } from 'react';

import { isOffline, isWorthRetrying, messageForFailure } from '@/api/failure';

/**
 * The parts every back-office list screen is built from.
 *
 * FE008 is the first of seven of these — outlets, terminals, users, assignments
 * and the rest are the same screen with different columns — and
 * `docs/ui-conventions.md` exists precisely because seven workers who cannot see
 * each other's branches will otherwise invent seven page headers, seven empty
 * states and seven ways of saying the network is down. The conventions document
 * settles what they should look like; this settles it in code, so the next
 * screen copies an import rather than a paragraph.
 *
 * Deliberately in `apps/web` and not in `packages/ui`. These are compositions of
 * registry primitives that know what a back-office screen is, which is app
 * knowledge; the registry components underneath them are the shared library's.
 * The conventions ask for a second consumer before something is promoted, and
 * the terminals — the other consumer — are a different shell entirely.
 *
 * ## The panel, the head and the field
 *
 * Revised 26 Aug 2026, and **this replaces the sheet** — 26 Aug, earlier the
 * same day, superseded here. `docs/design-language.md` records the same
 * supersession under **The panel and the rail**; read it there for the
 * language, and here for what the markup does about it.
 *
 * **The head stands and the sheet goes.** #174 bought the field all four edges
 * by drawing a bordered, rounded enamel box inside it. `BackOfficeShell` is now
 * a single rounded enamel panel on the mist ground, so those four edges are
 * already drawn one level up — and a bordered box inside a bordered panel is a
 * card inside a card. The head, the toolbar, the records and the foot are bands
 * of the panel's own field now, ruled from each other and from nothing else.
 *
 * The head is **exactly** what #174 built and it is not reopened: `4.5rem`, one
 * baseline row, the title, the optional `lede` behind a hairline divider, the
 * screen's one action carried to the end, and the datum sitting on the head's
 * foot hairline at the head's own left content edge. It worked and it survives
 * the change of ground under it unaltered.
 *
 * What changed is underneath:
 *
 * - **No border, no radius, no inset.** The three bands run the full width of
 *   the field and rule against each other in `border-border`.
 * - **Every band is on the head's own gutter** — `px-4`, `lg:px-7`. The sheet
 *   had its own `px-5` because it was a box inset from the frame; there is no
 *   box, so the extent is stated once and the toolbar, the records and the foot
 *   all start where the title starts.
 * - **`main` is `flex-1` again.** #174 had to state `lg:h-screen lg:flex-none`
 *   because `BackOfficeShell` was `min-h-screen` — its height was its content's,
 *   so a `flex-basis` of zero resolved against a number that was whatever the
 *   table happened to be. The panel states `h-[calc(100vh-28px)]` at the top of
 *   the chain, so the number is definite and `flex-1` is enough. **`min-h-0` is
 *   still needed on every link below it** and removing it puts the scroll back
 *   on the window.
 *
 * **A control on the field takes the mist fill.** Unchanged, and for the reason
 * it was given: `docs/design-language.md`, Structure, says a field is enamel on
 * mist and that reads because the ground is a step below the field. The panel
 * *is* enamel, so an enamel input on it is a button-shaped outline with nothing
 * in it. Controls in the toolbar are `bg-background`. The inversion is local to
 * the panel; `AuthFrame` and the signed-out page keep the mist ground and their
 * enamel fields, which is correct there, and `fieldSurface` is untouched.
 *
 * ## A form is not a list
 *
 * #174 was designed against tables and never opened a form screen, and it cost
 * something live: with the cap gone and the field taking the whole panel,
 * `RecordCorrection` rendered a **1520px-wide select** at 1728. A list wants the
 * width. A form wants a measure and does not want the rest.
 *
 * So `guidance` — one optional prop, and a screen that passes none keeps exactly
 * what it has today, which is what keeps this out of twenty screens. Given one,
 * the body is a row: the fields at `max-w-[36rem]` on the field's left edge, the
 * same edge as the title, and the guidance pinned to the field's **right** edge
 * at `20rem` behind a left hairline. Both anchored to an edge, so what is
 * between them reads as measure rather than as a hole — which is the register
 * this whole system is built on, matter left and standing reference right.
 *
 * **The foot goes with it, and `description` moves into the margin as the first
 * note.** A form's explanation belongs beside its fields; repeating it in a foot
 * under them is a second copy of the one sentence somebody reads once.
 */

/**
 * The page: a head, and the field under it.
 *
 * `action` is the screen's one accent, carried to the end of the head — the one
 * place on this page where a thing on the left and a thing on the right are a
 * **pair** rather than two strangers. The title names the field and the button
 * is the only thing you can do to it.
 *
 * `lede` and `guidance` are optional and `description` is not, which is what
 * keeps this change out of twenty other screens. A screen that says nothing gets
 * a head with just its title, which is correct and needs no edit; its
 * `description` sets in the foot wherever it already was in the props.
 */
export function Screen({
  title,
  lede,
  description,
  action,
  aside,
  guidance,
  children,
}: {
  title: string;
  /**
   * A short line beside the title — six or seven words, no full stop.
   *
   * Optional, and deliberately not a shortening of `description`: it answers
   * "what is this screen" at a glance for somebody who already knows, where
   * `description` answers it for somebody who does not. A screen with nothing
   * that short to say should say nothing.
   */
  lede?: string;
  /**
   * The sentence that explains the screen.
   *
   * It sets in the field's foot, end-aligned — or, on a screen that passes
   * `guidance`, as the first note in the margin, where there is no foot to put
   * it in and a form's explanation belongs beside its fields anyway.
   */
  description: string;
  action?: ReactNode;
  /** Filters and counts — the standing facts about the field, not controls on it. */
  aside?: ReactNode;
  /**
   * Standing reference beside a form: `ScreenNote`s, and whatever a screen wants
   * under them.
   *
   * **Passing this says "this screen is a form, not a list"** and is the whole
   * of the switch. It caps the children at a measure, pins this to the field's
   * right edge, and drops the foot in favour of `description` as the first note.
   * A list screen passes nothing and is untouched.
   */
  guidance?: ReactNode;
  children: ReactNode;
}) {
  return (
    // `flex-1` and no stated height. #174 needed `lg:h-screen lg:flex-none`
    // because the shell above it was `min-h-screen`: its height was its
    // content's, so this box's height was its content's, and `flex-1` resolved
    // against a number that was whatever the table happened to be. It held on
    // Staffing and failed on Cash book at 1280, where a toolbar wrapping to two
    // rows pushed the sheet 23px past the window.
    //
    // The panel states `h-[calc(100vh-28px)]` now, which is the definite number
    // the chain was missing, and it states it once at the top instead of here.
    // `min-h-0` is still load-bearing on this and on every link under it.
    //
    // Below `lg` the rail is a band above this rather than a column beside it
    // and the panel has no stated height, so the page scrolls — which is what a
    // page on a phone does.
    <main className="flex min-h-0 min-w-0 flex-1 flex-col">
      {/* The head, exactly as #174 built it. The panel under it is enamel and so
          is this, so the two read as one surface with a rule across it rather
          than as chrome sitting on a page.

          `h-[4.5rem]` and a single row, because everything on it is one line.
          The height is stated rather than derived so that the datum below has a
          fixed edge to sit on and every screen's head is the same head. */}
      <div className="relative flex h-[4.5rem] shrink-0 items-center gap-4 border-b border-border px-4 lg:px-7">
        <h1 className="shrink-0 font-heading text-[1.375rem] font-medium tracking-[-0.012em] text-foreground">
          {title}
        </h1>

        {lede !== undefined && (
          // Hidden below `sm`, where a 390px head holds a title and an action
          // and nothing else. It is a gloss on the title, so losing it costs
          // nothing the `description` still says.
          <div className="hidden min-w-0 items-center gap-4 sm:flex">
            <div className="h-[1.125rem] w-px shrink-0 bg-border" />
            <p className="truncate text-sm text-muted-foreground">{lede}</p>
          </div>
        )}

        {action !== undefined && <div className="ms-auto flex shrink-0">{action}</div>}

        {/* The datum. It was a stub under the title from `ChangeCredential`
            onwards and it is the same mark, sitting on the head's foot where it
            does structural work at no height at all: the hairline is the head's
            bottom edge and this is the ink on it, aligned to the head's own left
            content edge so it starts where the title starts.

            `-bottom-px` and `h-[2px]`, so it straddles the `1px` rule rather
            than sitting above it. There is no second stub; this is it. */}
        <div className="absolute -bottom-px left-4 h-[2px] w-10 bg-foreground lg:left-7" />
      </div>

      {/* The toolbar: what is standing true about the records, and what narrows
          them. It changed address and not behaviour — the split is still made by
          CSS rather than by a second prop, so no screen has to re-declare which
          of its children was a fact and which was a filter: `me-auto` on the
          last `ScreenFact` takes the free space after the facts, and everything
          after it is carried to the end of the bar.

          **`max-w-sm` on every item is a fix, not a preference.** A flex item is
          as wide as its widest content, and the rail this came from bounded that
          at `16rem` without anybody having to say so. Laid in a row and
          unbounded it is the longest sentence that decides the width: Tickets'
          "Trading day" carried a two-line explanation and stretched a day picker
          to 840px. `shrink-0` beside it, so a full line wraps to the next row
          rather than squashing its neighbour.

          `items-end`, so a lone select sits on the same baseline as the facts
          carried opposite it.

          **The controls recess here.** A field in this system is enamel on mist
          — `docs/design-language.md`, Structure — and that reads because the
          ground is a step below the field. The panel *is* enamel, so an enamel
          input on it is an outline with nothing in it. Stated once, on the
          toolbar, rather than as a className on every select in twenty screens:
          `bg-background` with the border the rest of the system draws.
          `DayPicker` states its own, because it states its own fill already and
          a descendant rule cannot beat a utility on the element itself. */}
      {aside !== undefined && (
        <div className="flex shrink-0 flex-wrap items-end gap-x-8 gap-y-3 border-b border-border px-4 py-3.5 lg:px-7 [&_[data-slot=input]]:border-border [&_[data-slot=input]]:bg-background [&_[data-slot=select-trigger]]:border-border [&_[data-slot=select-trigger]]:bg-background [&>*]:max-w-sm [&>*]:shrink-0 [&>[data-screen-fact]:not(:has(~[data-screen-fact]))]:me-auto">
          {aside}
        </div>
      )}

      {guidance === undefined ? (
        <>
          {/* The records. **This is the only thing on the page that scrolls**,
              and it scrolls in both directions: a table too wide for the field
              moves inside it rather than handing the window a horizontal
              scrollbar.

              `min-w-0` beside `min-h-0` for the same reason it was here before —
              without it a wide table makes this box grow to fit instead of
              letting it scroll. */}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-auto px-4 py-4 lg:px-7">
            {children}
          </div>

          {/* The foot: the sentence that explains the screen, available and out
              of the way. End-aligned and measured, so a long one sets to a
              readable line rather than running the field, and a short one sits
              under the end of the records where the eye already is.

              `description` renders here and no screen was edited to move it.
              That is the whole reason `lede` is a new prop rather than a
              re-purposing of this one. */}
          <div className="flex shrink-0 justify-end border-t border-border px-4 py-3 lg:px-7">
            <p className="max-w-[52rem] text-end text-[0.8125rem] text-muted-foreground">
              {description}
            </p>
          </div>
        </>
      ) : (
        // THE FORM CASE. Matter left at a measure, standing reference right at
        // the field's edge, and nothing stretched to fill what is between them.
        //
        // `justify-between` with a shrinkable `36rem` basis on the fields and a
        // fixed `20rem` on the margin: both are anchored to an edge, so the gap
        // reads as measure. Left to `flex-1` the fields would grow into it,
        // which is the 1520px select this exists to end.
        //
        // The row scrolls as one and there is no foot. A form is read top to
        // bottom with its reference beside it, and pinning the margin while the
        // fields move would leave a note about the third field level with the
        // first. Below `lg` it stacks and the hairline moves to the top of the
        // margin, because a `20rem` column beside a form on a 390px screen is
        // two unreadable columns.
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-auto lg:flex-row lg:justify-between lg:gap-16">
          <div className="min-w-0 px-4 py-6 lg:basis-[36rem] lg:px-7 lg:py-7">{children}</div>

          <div className="shrink-0 border-t border-border px-4 pt-6 pb-8 lg:w-80 lg:border-t-0 lg:border-l lg:ps-8 lg:pe-7 lg:pt-7">
            <div className="space-y-5">
              {/* `description` is the first note and the label is stated here
                  rather than by the caller: every screen's `description` answers
                  the same question, and letting twenty screens each name it is
                  how twenty screens end up with twenty labels for one thing. */}
              <ScreenNote label="What this is">{description}</ScreenNote>
              {guidance}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

/**
 * A standing note in a form's margin: signage label over prose.
 *
 * The same device as `ScreenFact` and `AuthFrame.Fact` and deliberately not a
 * third one — a label in caps over the thing it names, which is how a plate
 * reads. `ScreenFact` carries a value read at a glance; this carries a sentence
 * or two of standing reference, so it sets in prose at the caption size rather
 * than in body.
 *
 * `children` is a node and the body is a `div` rather than a `p`, because the
 * margin's later notes carry links and lists as often as they carry a sentence.
 */
export function ScreenNote({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="font-display text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase">
        {label}
      </p>
      <div className="mt-1.5 text-[0.8125rem] leading-relaxed text-foreground">{children}</div>
    </div>
  );
}

/**
 * A standing fact about the field, in the bar: a count, a total, a date.
 *
 * The label is signage — caps, tracked, `font-display` — which is the treatment
 * `docs/design-language.md` reserves for frame lettering and field labels. These
 * are one- and two-word labels on a plate, which is exactly the case it sanctions
 * and not the sentence case it forbids. Label over value survives the move out of
 * the rail: it is a plate, and a plate reads down.
 *
 * `data-screen-fact` is how the bar tells a fact from a filter without every
 * screen having to declare it. It is markup for layout and carries no meaning to
 * a reader, which is why it is a data attribute and not a role or a class the
 * caller could be tempted to key a test on.
 */
export function ScreenFact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div data-screen-fact>
      <p className="font-display text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase">
        {label}
      </p>
      <p className="mt-1 text-sm text-foreground">{children}</p>
    </div>
  );
}

/**
 * A table's shape while its rows are in flight.
 *
 * A skeleton and not a spinner, and one that matches the content: the rows are
 * the height they will be and the columns are the columns they will be, so the
 * page does not jump when the data lands. `docs/ui-conventions.md` asks for
 * exactly this, and the reason is that a layout shift under a pointer is how
 * somebody clicks the row below the one they aimed at.
 *
 * `aria-hidden` on the whole body, because there is nothing here to read: a
 * screen reader announcing four rows of placeholder cells describes furniture
 * that is about to be replaced. The table keeps its accessible name and its
 * headers throughout, which is what tells a reader where they are.
 */
export function TableSkeleton({ columns, rows = 4 }: { columns: number; rows?: number }) {
  return (
    <TableBody aria-hidden>
      {Array.from({ length: rows }, (_, row) => (
        <TableRow key={row}>
          {Array.from({ length: columns }, (_, column) => (
            <TableCell key={column} className="py-3.5">
              <div
                className="h-4 animate-pulse rounded-3xl bg-muted"
                // Varied widths, so it reads as text rather than as a bar
                // chart. Derived from the position rather than random, because
                // nothing in this app reads a clock or a random source
                // directly and a skeleton is no exception — a random width
                // would also re-roll on every render.
                style={{ width: `${String(45 + ((row * 3 + column * 7) % 45))}%` }}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );
}

/**
 * One row spanning the table, for the states where there are no rows.
 *
 * Inside the table rather than replacing it, so the column headers stay put and
 * the page does not change shape between loading, empty and loaded.
 *
 * **`whitespace-normal` is a fix, not a preference.** The registry's `TableCell`
 * sets `whitespace-nowrap`, which is right for a data cell and catastrophic for a
 * paragraph: the cell sizes to the full sentence, drags the table past the
 * viewport and hands the card a horizontal scrollbar. `max-w-md` on the inner div
 * cannot save it, because the cell has already been measured. Nothing warns you,
 * and it shipped.
 *
 * **An empty table gets no ruled lines under the notice, and that was tried.**
 * The idea was a register ruled to its full extent whether or not every line is
 * filled. In practice the rules are drawn the same way a real row divider is —
 * same colour, same pitch, same span — so they read as four rows that failed to
 * load, which is worse than empty space because it suggests a fault. If you are
 * about to add them back, that is why they went.
 *
 * **`data-table-notice` is how the records band knows to centre it.** The
 * records reach the bottom of the window now, so a `py-14` row at the top of
 * them leaves the one sentence on the screen stranded against the header with a
 * thousand pixels of nothing under it — which reads as a list that stopped
 * loading rather than as a list with nothing in it. `ScreenTable` keys on the
 * attribute and lets the table take the band's height in this state and this
 * state alone; a table with real rows keeps its natural height, because a
 * stretched table hands the spare pixels to its rows and eight outlets become
 * eight tall bands.
 */
export function TableNotice({ columns, children }: { columns: number; children: ReactNode }) {
  return (
    <TableBody data-table-notice>
      <TableRow className="hover:bg-transparent">
        <TableCell colSpan={columns} className="px-6 py-14 align-middle whitespace-normal">
          <div className="mx-auto max-w-md space-y-3 text-center">{children}</div>
        </TableCell>
      </TableRow>
    </TableBody>
  );
}

/**
 * Nothing here yet — said as what would be here and how to make the first one.
 *
 * Never a bare "No results". The conventions are blunt about this and the reason
 * is that these are setup screens: the first person to open one is a park
 * manager who has been handed a system with nothing in it, and "No results" is
 * a dead end for exactly the person who most needs a way forward.
 *
 * The description names the control by its label and does not say where it is.
 * "Use New outlet above" was the interface describing its own furniture, and it
 * goes stale the moment the button moves — which it just did.
 */
export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <>
      <p className="font-medium text-foreground">{title}</p>
      <p className="text-sm text-muted-foreground">{description}</p>
      {action !== undefined && <div className="!mt-5 flex justify-center">{action}</div>}
    </>
  );
}

/**
 * A load that failed: what happened, and what to do about it.
 *
 * The message comes from the code by way of `messageForFailure`, so no screen
 * words a refusal itself and none renders the server's prose. Retry is offered
 * only where retrying can help — the API's own registry decides that, and a
 * "try again" button under `AUTH_FORBIDDEN` is a control that cannot work.
 *
 * Offline gets its own icon and no alarm. On a hill station with intermittent
 * connectivity it is an operating mode, not a fault, and the back office says
 * plainly that it cannot read the list right now rather than announcing an
 * error.
 *
 * `title` is optional and defaults to the seven list screens' wording, which is
 * what every caller before FE046 wanted. Day Close is not a list — it reads one
 * day and its reconciliation — and "this list could not be loaded" there is a
 * screen describing furniture it does not have. The alternative was a second
 * copy of the offline branch and the retryable test beside it, which is how two
 * screens end up disagreeing about whether offline is an error.
 */
export function LoadFailed({
  error,
  onRetry,
  title = 'This list could not be loaded',
}: {
  error: unknown;
  onRetry: () => void;
  title?: string;
}) {
  const offline = isOffline(error);

  return (
    <>
      <p className="flex items-center justify-center gap-2 font-medium text-foreground">
        {offline ? (
          <IconWifiOff className="size-4 shrink-0" aria-hidden />
        ) : (
          <IconAlertTriangle className="size-4 shrink-0" aria-hidden />
        )}
        {offline ? 'No connection to the server' : title}
      </p>

      {/* `role="status"` rather than `alert`: it is announced without
          interrupting, and a screen that failed to load has not done anything
          the reader must be pulled away from. */}
      <p role="status" className="text-sm text-muted-foreground">
        {messageForFailure(error)}
      </p>

      {isWorthRetrying(error) && (
        <div className="!mt-5 flex justify-center">
          <Button variant="outline" onClick={onRetry}>
            Try again
          </Button>
        </div>
      )}
    </>
  );
}

/**
 * The table itself, named for the people who navigate by landmark.
 *
 * A bare `<table>` is announced as "table" and nothing else, which in a back
 * office of seven near-identical list screens is no help at all. The caption is
 * visually hidden rather than absent so the name is the same one on screen.
 *
 * ## `columns`, and why it is a choice rather than a rule
 *
 * FE066 tried twice to settle column widths for all twenty screens at once and
 * cut both attempts — `docs/design-language.md`, under **Open**, records what
 * they were and what they broke. The finding was that **a table ending in an
 * amount and a table ending in text want opposite things**, and nothing this
 * component can see tells it which one it is holding. So it does not guess: it
 * takes the answer from the screen, and `measured` stays the default so the
 * nineteen screens that never say anything keep exactly what they had.
 */
export function ScreenTable({
  label,
  columns = 'measured',
  children,
}: {
  label: string;
  /**
   * How the column widths are decided.
   *
   * - **`measured`** (the default) — automatic layout. Content decides, and any
   *   width a screen states is a suggestion the browser weighs against the
   *   content and the leftover space. This is right for a table whose last
   *   column is an amount: the row reaches the field's edge and the figures
   *   right-align against it, which is what `docs/ui-conventions.md` asks for.
   * - **`stated`** — fixed layout. A width means that width, and **the one
   *   column that states none takes whatever is left**, so the slack falls at
   *   the end of the row instead of swelling a column in the middle of it. Right
   *   for a table that ends in text, where nothing needs the edge.
   *
   * A screen asking for `stated` must state a width on every column but one.
   * State them all and the leftover is shared out again, which is the thing this
   * was chosen to stop.
   */
  columns?: 'measured' | 'stated';
  children: ReactNode;
}) {
  return (
    // **No card.** The table used to sit on one, because the page ground was a
    // step below it and that step was the only thing the surface read by. It is
    // on the panel's own field now, and a card here is a card inside a card —
    // twice over, since the panel is the second one.
    //
    // What is left is the three things the table needs from the field and
    // cannot state itself:
    //
    // - **A negative margin, to bleed back out of the records' padding.** Row
    //   dividers read as a ruled register only if they run the full width of the
    //   field; inset by the padding they read as an underlined block. The
    //   matching `ps`/`pe` on the first and last cells then puts the content
    //   back on the same left edge as the head and the toolbar above it, so the
    //   extent is stated once and the interior column spacing is untouched. The
    //   figures track the records' own gutter — `4`, and `7` at `lg` — which is
    //   the head's gutter, not the sheet's old `5`.
    // - **A sticky head.** `top-0` against the records' own scroll, filled
    //   `bg-card` so rows pass under it rather than through it, and its rule
    //   drawn as an inset shadow: `border-collapse` is the browser default and a
    //   collapsed bottom border on a sticky `thead` is not painted.
    // - **`overflow-visible` on the registry's table container.** It is
    //   `overflow-x-auto`, which makes it the nearest scrolling ancestor and
    //   leaves a sticky head sticking to a box that never scrolls. The records
    //   scroll in both directions now, so this container has nothing left to do.
    <div className="-mx-4 has-[[data-table-notice]]:flex has-[[data-table-notice]]:min-h-0 has-[[data-table-notice]]:flex-1 has-[[data-table-notice]]:*:w-full lg:-mx-7 [&_[data-slot=table-container]]:overflow-visible [&_td:first-child]:ps-4 lg:[&_td:first-child]:ps-7 [&_td:last-child]:pe-4 lg:[&_td:last-child]:pe-7 [&_th:first-child]:ps-4 lg:[&_th:first-child]:ps-7 [&_th:last-child]:pe-4 lg:[&_th:last-child]:pe-7 [&_thead]:sticky [&_thead]:top-0 [&_thead]:z-10 [&_thead_th]:bg-card [&_thead_th]:shadow-[inset_0_-1px_0_var(--border)] [&_thead_tr]:border-b-0">
      <Table
        className={[
          // `w-max min-w-full` beside `table-fixed`, and it is a fix rather than
          // a flourish. Fixed layout honours a stated width **only while the
          // table has the room**: at 390 the registry's `w-full` held the table
          // to the field, the five stated widths were scaled down to fit, and
          // the cells — `whitespace-nowrap` — painted over one another. Sizing
          // to `max-content` lets the stated widths mean what they say and hands
          // the overflow to the records, which scroll in both directions;
          // `min-w-full` keeps the table filling the field at every width where
          // it fits, which is all of them above `sm`.
          columns === 'stated' ? 'w-max min-w-full table-fixed' : '',
          // Only while a notice is showing. `h-full` on a table with rows in it
          // hands the spare height out across those rows.
          'has-[[data-table-notice]]:h-full',
        ]
          .join(' ')
          .trim()}
      >
        <caption className="sr-only">{label}</caption>
        {children}
      </Table>
    </div>
  );
}
